import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef, useState } from "react";
import {
    FaBalanceScale,
    FaBan,
    FaCheckCircle,
    FaEdit,
    FaFileContract,
    FaGavel,
    FaGlobe,
    FaHandshake,
    FaShieldAlt,
    FaUserCheck
} from "react-icons/fa";
import {
    TiArrowRight,
    TiArrowUp
} from "react-icons/ti";
import AnimatedTitle from "./AnimatedTitle";
import Footer from "./Footer";
import ScrollDownButton from './ScrollDownButton';
import GlassButton from './ui/GlassButton';
import { SparklesCore } from './ui/SparklesCore';

gsap.registerPlugin(ScrollTrigger);

const TermsOfUse = () => {
  const [scrollDownClicked, setScrollDownClicked] = useState(false);
  const [isBottom, setIsBottom] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const sectionRefs = useRef([]);
  const scrollDownRef = useRef(null);
  const scrollTopRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    sectionRefs.current.forEach((section, index) => {
      if (section) {
        gsap.fromTo(
          section,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            delay: index * 0.1,
            scrollTrigger: {
              trigger: section,
              start: "top 90%",
            },
          }
        );
      }
    });

    if (scrollDownRef.current) {
      gsap.fromTo(
        scrollDownRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 1 }
      );
    }
  }, []);

  const checkIfBottom = () => {
    if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 200) {
      setIsBottom(true);
    } else {
      setIsBottom(false);
    }
  };

  const scrollToContent = () => {
    const content = document.getElementById("content");
    if (content) {
      const contentOffsetTop = content.offsetTop;
      window.scrollTo({
        top: contentOffsetTop - 100,
        behavior: "smooth",
      });
    }
    setScrollDownClicked(true);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setScrollDownClicked(false);
  };

  useEffect(() => {
    window.addEventListener("scroll", checkIfBottom);
    return () => {
      window.removeEventListener("scroll", checkIfBottom);
    };
  }, []);

  const termsPrinciples = [
    {
      id: "TA-01",
      title: "Platform Access",
      subtitle: "Sovereign Entry Protocol",
      desc: "By accessing the Zero-Ecosystem, you agree to these terms and all applicable regulations. Access is granted for legitimate use aligned with our institutional standards.",
      icon: <FaUserCheck />,
      highlights: ["Legitimate Use", "Institutional Standards", "Global Access"]
    },
    {
      id: "TA-02",
      title: "User Responsibilities",
      subtitle: "Accountability Framework",
      desc: "Users must provide accurate information, maintain account security, and comply with all applicable laws. Misrepresentation will result in immediate suspension.",
      icon: <FaBalanceScale />,
      highlights: ["Accurate Information", "Account Security", "Legal Compliance"]
    },
    {
      id: "TA-03",
      title: "Prohibited Activities",
      subtitle: "Enforcement Protocol",
      desc: "Malicious activities including hacking, fraud, money laundering, and platform manipulation are strictly prohibited. Violators face permanent bans and legal action.",
      icon: <FaBan />,
      highlights: ["Anti-Fraud", "Anti-Manipulation", "Legal Enforcement"]
    },
    {
      id: "TA-04",
      title: "Liability Framework",
      subtitle: "Risk Acknowledgment",
      desc: "The Zero-Ecosystem is provided 'as-is'. We are not liable for market volatility, third-party actions, or losses incurred through platform use. Invest responsibly.",
      icon: <FaGavel />,
      highlights: ["As-Is Provision", "Risk Awareness", "Responsible Use"]
    },
    {
      id: "TA-05",
      title: "Terms Modification",
      subtitle: "Amendment Protocol",
      desc: "We reserve the right to modify these terms at any time. Continued use constitutes acceptance. Major changes will be communicated through official channels.",
      icon: <FaEdit />,
      highlights: ["Dynamic Terms", "Continued Use", "Official Channels"]
    },
    {
      id: "TA-06",
      title: "Dispute Resolution",
      subtitle: "Arbitration Framework",
      desc: "All disputes shall be resolved through binding arbitration in accordance with international blockchain arbitration standards. Jurisdiction: United Kingdom.",
      icon: <FaHandshake />,
      highlights: ["Binding Arbitration", "UK Jurisdiction", "Fair Resolution"]
    }
  ];

  const effectiveDate = "January 1, 2026";
  const lastUpdated = "January 7, 2026";

  return (
    <div className="w-screen min-h-screen bg-black text-blue-50 font-sans">
      {/* Hero Section */}
      <div className="relative w-full h-screen flex flex-col items-center justify-center p-5">
        <div className="relative w-full max-w-6xl h-64 md:h-[500px] overflow-hidden rounded-[4rem] border border-white/10 z-10 bg-black shadow-[0_0_80px_rgba(34,211,238,0.15)]">
          <video
            src="/videos/terms-hero.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="absolute left-0 top-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <SparklesCore
              id="termsHeroSparkles"
              background="transparent"
              minSize={0.6}
              maxSize={1.4}
              particleDensity={50}
              particleColor="#22d3ee"
              className="absolute inset-0"
            />
            <AnimatedTitle
              title="Sovereign Agreement"
              containerClass="text-center text-blue-50 font-zentry relative z-10"
            />
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-cyan-400/80 font-bold tracking-[0.4em] uppercase text-[10px] mt-6 z-10"
            >
              Institutional Terms & Conditions Protocol
            </motion.p>
          </div>
        </div>
      </div>

      {/* Scroll Down Button */}
      {!scrollDownClicked && (
        <ScrollDownButton
          ref={scrollDownRef}
          onClick={scrollToContent}
        />
      )}

      {/* Content Section */}
      <div id="content" className="px-6 md:px-10 py-32 mx-auto max-w-7xl">
        
        {/* Agreement Header */}
        <div className="mb-24 p-12 rounded-[4rem] bg-gradient-to-r from-emerald-500/10 to-transparent border border-white/10">
           <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
              <div>
                 <div className="text-[10px] font-bold text-emerald-500 uppercase tracking-[0.4em] mb-4">Legal Framework</div>
                 <h2 className="text-3xl md:text-4xl font-bold text-white tracking-widest leading-tight">Terms of Service Agreement</h2>
              </div>
              <div className="flex flex-col gap-2 text-right">
                 <div className="text-[9px] font-bold text-gray-600 uppercase tracking-widest">Effective Date</div>
                 <div className="text-sm font-bold text-white">{effectiveDate}</div>
                 <div className="text-[9px] font-bold text-gray-600 uppercase tracking-widest mt-2">Last Updated</div>
                 <div className="text-sm font-bold text-emerald-400">{lastUpdated}</div>
              </div>
           </div>
        </div>

        {/* Terms Principles Bento */}
        <div className="mb-48">
           <div className="mb-16 flex flex-col md:flex-row justify-between items-end gap-8">
              <div className="max-w-xl">
                 <div className="text-[10px] font-bold text-teal-500 uppercase tracking-[0.4em] mb-4">Binding Directives</div>
                 <h2 className="text-4xl md:text-5xl font-bold text-white tracking-widest leading-tight">Agreement Principles</h2>
              </div>
              <p className="text-gray-300 italic text-sm max-w-xs md:text-right">
                 By using any Zero-Ecosystem service, you acknowledge and accept these terms.
              </p>
           </div>

           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {termsPrinciples.map((principle, index) => (
                <motion.div
                  key={principle.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="relative group rounded-[3rem] bg-white/5 border border-white/10 hover:border-emerald-500/30 transition-all hover:bg-white/10 overflow-hidden flex flex-col p-10"
                  ref={(el) => (sectionRefs.current[index] = el)}
                >
                  <div className="absolute top-8 right-10 text-4xl text-emerald-500/20 group-hover:scale-110 transition-transform">
                     {principle.icon}
                  </div>
                  <div className="text-[9px] font-bold text-gray-500 uppercase tracking-widest mb-2">{principle.id}</div>
                  <h3 className="text-xl font-bold text-white mb-2 tracking-widest">{principle.title}</h3>
                  <div className="text-[10px] font-bold text-emerald-400 uppercase tracking-[0.2em] mb-6">{principle.subtitle}</div>
                  <p className="text-gray-300 text-xs leading-relaxed mb-8 flex-grow">{principle.desc}</p>
                  
                  <div className="flex flex-wrap gap-2 mt-auto">
                     {principle.highlights.map((h, i) => (
                       <span key={i} className="text-[8px] font-bold text-gray-600 border border-white/5 px-3 py-1 rounded-full uppercase tracking-widest group-hover:border-emerald-500/20 group-hover:text-gray-300 transition-colors">
                          {h}
                       </span>
                     ))}
                  </div>
                </motion.div>
              ))}
           </div>
        </div>

        {/* Agreement Acceptance Protocol */}
        <div className="mb-48">
           <div className="p-2 rounded-[4rem] bg-gradient-to-br from-emerald-500/20 to-transparent border border-white/10 overflow-hidden shadow-2xl">
              <div className="bg-black/80 backdrop-blur-3xl p-16 relative">
                 <div className="flex flex-col lg:flex-row gap-16 items-center">
                    <div className="flex-1">
                       <div className="flex items-center gap-4 mb-8">
                          <div className="w-16 h-16 rounded-2xl bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-3xl">
                             <FaFileContract />
                          </div>
                          <div>
                             <div className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.4em]">Agreement Protocol</div>
                             <h3 className="text-2xl font-bold text-white tracking-widest">Terms Acceptance</h3>
                          </div>
                       </div>
                       <p className="text-gray-300 text-sm leading-relaxed mb-8 italic">
                          By continuing to use the Zero-Blockchain ecosystem, including all products, services, and platforms, you confirm that you have read, understood, and agree to be bound by these Terms of Service.
                       </p>
                       <div className="grid grid-cols-2 gap-6">
                          {[
                            { l: "Jurisdiction", v: "United Kingdom" },
                            { l: "Governing Law", v: "UK Financial Law" },
                            { l: "Arbitration", v: "International Standard" },
                            { l: "Amendment Rights", v: "Reserved" }
                          ].map((item, i) => (
                            <div key={i} className="flex flex-col gap-1">
                               <div className="text-[8px] font-bold text-gray-600 uppercase tracking-widest">{item.l}</div>
                               <div className="text-sm font-bold text-white italic tracking-wider">{item.v}</div>
                            </div>
                          ))}
                       </div>
                    </div>

                    <div className="flex-shrink-0 flex flex-col items-center gap-8">
                       <motion.button
                         onClick={() => setAccepted(!accepted)}
                         whileHover={{ scale: 1.05 }}
                         whileTap={{ scale: 0.95 }}
                         className={`w-32 h-32 rounded-[2rem] border-2 flex items-center justify-center text-5xl transition-all ${
                           accepted 
                             ? 'bg-emerald-600 border-emerald-400 text-white shadow-[0_0_40px_rgba(16,185,129,0.4)]' 
                             : 'bg-white/5 border-white/20 text-gray-600 hover:border-emerald-500/50'
                         }`}
                       >
                          <FaCheckCircle />
                       </motion.button>
                       <div className="text-center">
                          <div className={`text-sm font-bold uppercase tracking-widest ${accepted ? 'text-emerald-400' : 'text-gray-600'}`}>
                             {accepted ? 'Terms Accepted' : 'Click to Accept'}
                          </div>
                          <div className="text-[9px] text-gray-600 mt-2">Binding Agreement</div>
                       </div>
                    </div>
                 </div>
              </div>
           </div>
        </div>

        {/* Final CTA */}
        <motion.div
           initial={{ opacity: 0, scale: 0.95 }}
           whileInView={{ opacity: 1, scale: 1 }}
           viewport={{ once: true }}
           className="relative text-center py-48"
        >
           <div className="absolute inset-0 bg-emerald-500/5 rounded-[100%] blur-[150px]" />
           <div className="relative z-10 max-w-4xl mx-auto px-6">
              <div className="w-24 h-24 rounded-[3rem] bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-5xl text-emerald-400 mx-auto mb-16 shadow-[0_0_50px_rgba(16,185,129,0.2)]">
                <FaGavel />
              </div>
              <h2 className="text-5xl md:text-7xl font-bold mb-12 leading-tight">
                 Initialize <span className="font-zentry italic text-emerald-400 text-8xl uppercase tracking-tighter block mt-4">Sovereign</span> Access
              </h2>
              <p className="text-gray-300 text-xl leading-relaxed mb-20 max-w-2xl mx-auto italic">
                Your agreement enables full access to the Zero-Ecosystem's institutional-grade services and products.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-8 justify-center items-center">
                 <GlassButton
                   variant="emerald"
                   size="lg"
                   className="px-16 py-8 text-xl"
                   onClick={() => {}}
                 >
                   Download Terms (.PDF)
                 </GlassButton>
                 
                 <GlassButton
                   variant="gray"
                   size="lg"
                   className="px-16 py-8 text-xl"
                   onClick={() => {}}
                 >
                   <div className="flex items-center gap-4 group">
                     Contact Legal Hub <TiArrowRight className="text-2xl text-emerald-400 group-hover:translate-x-2 transition-transform" />
                   </div>
                 </GlassButton>
              </div>

              <div className="mt-32 flex flex-wrap justify-center gap-16 opacity-30 grayscale hover:grayscale-0 transition-all duration-700">
                 <div className="flex items-center gap-2 font-bold tracking-[0.3em] text-[9px]"><FaGlobe /> UK JURISDICTION</div>
                 <div className="flex items-center gap-2 font-bold tracking-[0.3em] text-[9px]"><FaShieldAlt /> LEGALLY BINDING</div>
                 <div className="flex items-center gap-2 font-bold tracking-[0.3em] text-[9px]"><FaHandshake /> FAIR ARBITRATION</div>
              </div>
           </div>
        </motion.div>

      </div>

      {/* Back to Top */}
      {isBottom && (
        <div
          ref={scrollTopRef}
          className="fixed bottom-10 left-5 flex items-center gap-3 px-5 py-3 bg-black/40 backdrop-blur-sm border border-emerald-500/30 rounded-full shadow-lg shadow-emerald-500/10 cursor-pointer hover:bg-black/60 hover:border-emerald-400/50 transition-all duration-500 z-50 group"
          onClick={scrollToTop}
        >
          <TiArrowUp className="text-emerald-400 group-hover:-translate-y-1 transition-transform duration-500" size={24} />
          <span className="relative z-20 text-sm uppercase text-white/80 font-medium tracking-wider">BACK TO TOP</span>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default TermsOfUse;
